import { STUDY_DEMO_CASES, demoWorkingResult, type StudyDemoCase } from './exampleData';
import type { TutorReply } from '@/pages/learn/study/tutorChat';
import type { TutorThreadView, TutorTurnView } from '@/pages/learn/study/studyTutorPanelApi';

export const DEMO_TUTOR_NOTICE =
  'Pre-generated Tutor demo · fictional learner · no live AI, messages or Stars. These replies explain the prepared example, not your current edits.';
const text = (value: string): TutorReply['blocks'][number] => ({ type: 'text', text: value });
const math = (latex: string): TutorReply['blocks'][number] => ({
  type: 'math',
  latex,
  display: true,
});
const followUps: Record<string, { question: string; blocks: TutorReply['blocks'] }> = {
  linear: {
    question: 'If I change the right side to 3, can I use x = 0.33?',
    blocks: [
      text(
        'Keep the exact fraction until the final display. Subtract 2 from both sides, then divide by 3.',
      ),
      math('3x+2=3\\;\\Rightarrow\\;3x=1\\;\\Rightarrow\\;x=\\frac{1}{3}'),
      math('3\\times\\frac{1}{3}+2=3'),
      text(
        'The exact check succeeds. With the rounded value 0.33, the left side is 2.99, not 3. Write x = 1/3 = 0.(3), where parentheses mark recurring digits; 0.33 is only an approximation. On the graph, the line y = 3x + 2 meets y = 3 at x = 1/3.',
      ),
    ],
  },
  cone: {
    question: 'Why is the slant height different from h? What changes if h becomes 12 cm?',
    blocks: [
      text(
        'The perpendicular height meets the base at 90°. The slant runs along the cone surface. Radius and height are the two legs of a right triangle; the slant is its hypotenuse.',
      ),
      math('l=\\sqrt{3^2+12^2}=\\sqrt{153}=3\\sqrt{17}\\approx12.3693\\text{ cm}'),
      math('V=\\frac13\\pi(3)^2(12)=36\\pi\\approx113.097\\text{ cm}^3'),
      math('S=\\pi r^2+\\pi rl=9\\pi+9\\pi\\sqrt{17}\\approx144.852\\text{ cm}^2'),
      text(
        'The base area stays 9π cm² because the radius is unchanged. Tripling the height triples volume, but surface area does not triple: it depends on the slant length and includes the unchanged base. Use h for volume and l for curved area.',
      ),
    ],
  },
  fraction: {
    question:
      'Why is 4/9 not exactly 44%? And is the first share of a 2:3 ratio two thirds of the whole?',
    blocks: [
      math(
        '\\frac49=0.\\overline4,\\qquad\\frac49\\times100\\%=\\frac{400}{9}\\%=44.\\overline4\\%',
      ),
      text(
        '44% is a rounded approximation, not the exact percentage. Rounding 4/9 to 0.44 before multiplying loses recurring digits. Keep 4/9 or 44.(4)% exact during working.',
      ),
      text(
        'For a 2:3 split, the whole has 2 + 3 = 5 equal parts. The first share is 2/5 of the whole; 2/3 compares the first share with the second share.',
      ),
      math('40\\div5=8,\\qquad A=2\\times8=16,\\qquad B=3\\times8=24'),
      text(
        'Check both conditions: 16 + 24 = 40 and 16:24 simplifies to 2:3. The selected-to-whole ratio is 2:5. This check distinguishes a part-to-part comparison from a part-to-whole fraction.',
      ),
    ],
  },
};
const order = ['linear', 'cone', 'fraction'];
const ordered = [...STUDY_DEMO_CASES].sort((a, b) => {
  const rank = (key: string) =>
    order.includes(key)
      ? order.indexOf(key)
      : 3 + STUDY_DEMO_CASES.findIndex((item) => item.key === key);
  return rank(a.key) - rank(b.key);
});
export function tutorExample(item: StudyDemoCase) {
  const result = demoWorkingResult(item);
  const id = item.workingId.replace('a1b0', 'd1b0');
  const pairs = [
    {
      question: item.question,
      blocks: [
        text(`Inputs: ${item.inputLabel}\nResult: ${result.outcome}`),
        ...result.steps.map((step, index) =>
          text(`${index + 1}. ${step.title}\n${step.formula}\n${step.explanation}`),
        ),
        text(`Check and meaning: ${result.check}`),
      ],
    },
    ...(followUps[item.key] ? [followUps[item.key]] : []),
  ];
  const turns: TutorTurnView[] = pairs.map((pair, index) => ({
    turn_id: `${id}-${index + 1}`,
    status: 'delivered',
    page_kind: 'subject_overview',
    context_label: item.title,
    learner_message: pair.question,
    reply: { format_version: 1, blocks: pair.blocks, suggested_questions: [] },
    actions: [],
    action_results: [],
    stars_charged: 0,
    stars_refunded: 0,
    error_code: null,
    truncated: [],
    created_at: '2026-10-17T04:00:00.000Z',
  }));
  const thread: TutorThreadView = {
    id,
    subject_key: 'mathematics',
    title: item.title,
    status: 'active',
    message_count: turns.length,
    stars_used: 0,
    preset_revision: null,
    created_at: '2026-10-17T04:00:00.000Z',
    last_message_at: '2026-10-17T04:00:00.000Z',
  };
  return { item, thread, turns };
}
export const STUDY_DEMO_TUTOR_EXAMPLES = ordered.map(tutorExample);
export function demoTutorForContext(question?: string, toolId?: string) {
  return (
    STUDY_DEMO_TUTOR_EXAMPLES.find((example) => example.item.title === question) ??
    STUDY_DEMO_TUTOR_EXAMPLES.find((example) => toolId?.endsWith(example.item.tool)) ??
    STUDY_DEMO_TUTOR_EXAMPLES[0]
  );
}
